import { Popper } from "@mui/base/Popper";
import { useAnimateHeight } from "@redotech/react-animation/transition";
import { useSize } from "@redotech/react-util/size";
import * as classnames from "classnames";
import { ForwardedRef, ReactNode, forwardRef, memo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import * as dropdownCss from "./dropdown.module.css";

export const Dropdown = memo(
  forwardRef(
    (
      {
        anchor,
        children,
        open,
        fitToAnchor = true,
        flip = true,
        placement = undefined,
      }: {
        anchor: HTMLElement | null;
        children: ReactNode | ReactNode[];
        open: boolean;
        fitToAnchor?: boolean;
        flip?: boolean;
        placement?: string;
      },
      ref: ForwardedRef<HTMLDivElement>,
    ) => {
      const [dropdown, setDropdown] = useState<HTMLElement | null>(null);

      const [animateStyles, animating] = useAnimateHeight(dropdown);

      const debounceOpen = useDebounce(open, 1);

      const size = useSize(anchor);

      const width = fitToAnchor ? size && `${size.width}px` : undefined;

      return (
        <Popper
          open={open || debounceOpen || animating}
          anchorEl={anchor}
          placement={placement as any} // Avoid cursed type check error when trying to import the PopupPlacement type
          modifiers={[{ name: "flip", enabled: flip }]}
          ref={ref}
        >
          <div
            className={classnames(dropdownCss.dropdown, {
              [dropdownCss.leave]: !open || !debounceOpen,
            })}
            ref={setDropdown}
            style={{ ...animateStyles, width }}
          >
            {(animating || (open && debounceOpen)) && children}
          </div>
        </Popper>
      );
    },
  ),
);

export const DropdownOption = memo<{
  action?: () => void;
  children: ReactNode | ReactNode[];
  fontFamily?: string;
  disabled?: boolean;
  stopPropagation?: boolean;
}>(({ action, children, fontFamily, disabled, stopPropagation }) => {
  return (
    <button
      className={dropdownCss.option}
      onClick={(event) => {
        stopPropagation && event.stopPropagation();
        action && action();
      }}
      style={{ fontFamily: fontFamily }}
      disabled={disabled}
      type="button"
    >
      {children}
    </button>
  );
});
